/**
 * Image Optimization Service
 * Client-side service for manual image conversion to WebP
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';

export interface ConvertImageResponse {
  success: boolean;
  originalPath: string;
  webpPath: string;
  originalSize: number;
  webpSize: number;
  compressionRatio: string;
}

/**
 * Manually convert an existing image to WebP format
 * Useful for converting images that were uploaded before the auto-conversion was implemented
 *
 * @param filePath - Path to the image in Firebase Storage (e.g., 'barbers/photo.jpg')
 * @returns Conversion result with file paths and compression stats
 */
export async function convertImageToWebP(filePath: string): Promise<ConvertImageResponse> {
  const convertImage = httpsCallable<{ filePath: string }, ConvertImageResponse>(
    functions,
    'convertImageToWebP'
  );

  try {
    const result = await convertImage({ filePath });
    return result.data;
  } catch (error: any) {
    console.error('Error converting image:', error);
    throw new Error(error.message || 'Failed to convert image to WebP');
  }
}

/**
 * Helper function to get the WebP path from an original image path
 *
 * @param originalPath - Original image path
 * @returns WebP version path
 */
export function getWebPPath(originalPath: string): string {
  // Don't convert SVG files
  if (originalPath.endsWith('.svg')) {
    return originalPath;
  }

  // If already WebP, return as is
  if (originalPath.endsWith('.webp')) {
    return originalPath;
  }

  // Replace extension with .webp
  const lastDotIndex = originalPath.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return originalPath;
  }

  return originalPath.substring(0, lastDotIndex) + '.webp';
}

/**
 * Check if a file is an image that can be converted to WebP
 *
 * @param filename - File name or path
 * @returns true if the file can be converted to WebP
 */
export function isConvertibleImage(filename: string): boolean {
  const convertibleExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.tiff', '.bmp'];
  const lowerFilename = filename.toLowerCase();
  return convertibleExtensions.some(ext => lowerFilename.endsWith(ext));
}

/**
 * Format file size to human-readable format
 *
 * @param bytes - Size in bytes
 * @returns Formatted size string (e.g., "1.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}
